import { ColorPalette, type ColorPaletteSerialized } from './ColorPalette.ts';
import { ColorPicker } from './ColorPicker.ts';
import {
    type ColorPaletteType,
    colors,
    type ColorSerialized,
    colorToJson,
    deserializeColor,
    type IndexedRGBColor,
    isIndexedColor,
    type RGBColor, rgbToHex
} from './colors.ts';
import { type SerializationContext, SerializationTypeError } from './errors.ts';
import { EventEmitter } from './EventEmitter.ts';
import { Logger } from './Logger.ts';
import { findElement, parseTemplate } from './utils-dom.ts';
import {
    CodeGenerationDetailLevel,
    type CodeGenerationOptions,
    type ColorPaletteSetStats,
    type DisplayModeColorValue,
    formatAssemblyNumber,
    generateId,
    type PaletteColorIndex,
    type StatsReceiver
} from './utils.ts';

export interface ColorPaletteSetOptions {
    id?: ColorPaletteSet['id'];
    mountEl: HTMLElement;
    backgroundColor?: ColorSerialized;
    palettes?: ColorPalette[] | ColorPaletteSerialized[];
    name?: string;
    type: ColorPaletteType;
}

export interface ColorPaletteSetSerialized {
    id: string | number;
    name: ColorPaletteSet['name'];
    backgroundColor: ColorSerialized;
    palettes: ColorPaletteSerialized[];
    type?: ColorPaletteType; // not present in legacy
}

const paletteSetTmpl = `
<div class="palette-set-container">
    <div class="palette-set-info" title="Manage palette sets">
        <div class="palette-set-info-top">
            <span class="palette-set-name clamp-1"></span>
            <i class="fa-solid fa-caret-down caret"></i>
        </div>
        <div class="palette-set-info-bottom">
            <div class="object-count text-muted"></div>
        </div>
    </div>
    <div class="palette-list">
        <div class="color-palette-container bg-color-container">
            <header class="color-palette-name">BG</header>
            <div class="color-swatch-list">
                <div class="color-swatch selectable" data-index="0"></div>
            </div>
        </div>
    </div>
</div>
`;

export type ColorPaletteSetEventMap = {
    bg_select: [ IndexedRGBColor ];
    color_change: [ ColorPalette, IndexedRGBColor, PaletteColorIndex ];
    overflow_click: [ HTMLElement ];
};

export class ColorPaletteSet extends EventEmitter<ColorPaletteSetEventMap> implements StatsReceiver<ColorPaletteSetStats> {
    private readonly palettes: ColorPalette[] = [];
    private backgroundColor: IndexedRGBColor;

    private readonly $container: HTMLElement;
    private readonly $el: HTMLElement;
    private readonly $bgSwatch: HTMLElement;
    private readonly $dropdownLauncher: HTMLElement;
    private readonly logger: Logger;
    private initialized = false;
    private name: string;
    public readonly id: string;
    private currentStats: ColorPaletteSetStats = {
        objectCount: 0,
    };
    private type: ColorPaletteType;

    private static instanceCount = 0;

    public constructor(options: ColorPaletteSetOptions) {
        super();
        ColorPaletteSet.instanceCount++;

        this.id = options.id || generateId();
        this.name = options.name || `Palette Set ${ColorPaletteSet.instanceCount}`;
        this.type = options.type;
        this.backgroundColor = deserializeColor(options.backgroundColor, this.type, colors[3]);
        this.logger = Logger.from(this);

        const palettes = Array.isArray(options.palettes) ?
            options.palettes.map((palette) => {
                if (palette instanceof ColorPalette) {
                    return palette;
                }

                return new ColorPalette({
                    colors: palette.colors,
                    id: String(palette.id),
                    name: palette.name,
                    type: this.type,
                });
            }) :
            [];

        while (palettes.length < 8) {
            palettes.push(new ColorPalette({
                name: 'P' + palettes.length,
                type: this.type,
            }));
        }

        this.palettes = palettes;

        this.$container = options.mountEl;
        this.$el = parseTemplate(paletteSetTmpl);
        this.$bgSwatch = findElement(this.$el, '.bg-color-container .color-swatch');
        this.$dropdownLauncher = findElement(this.$el, '.palette-set-info');
    }

    public getPalettes(): ColorPalette[] {
        return this.palettes;
    }

    public findPaletteById(paletteId: string | number): ColorPalette | null {
        return this.palettes.find(palette => palette.id === String(paletteId)) || null;
    }

    public getBackgroundColor(): RGBColor {
        return this.backgroundColor;
    }

    public getName(): string {
        return this.name;
    }

    public get stats(): Readonly<ColorPaletteSetStats> {
        return this.currentStats;
    }

    public getShortName(): string {
        return this.name
            .split(' ')
            .map(word => word[0]?.toUpperCase() || '')
            .filter(Boolean)
            .slice(0, 3)
            .join('');
    }

    public getGradientCSS(): string {
        const numColors = this.palettes.reduce((count, palette) => count + palette.colors.length, 0);
        const bgSize = 10;
        const paletteColorSize = (100 - bgSize) / numColors;

        let count = 0;
        const paletteColors = this.palettes
            .map((palette) => palette.colors.map(color => [ color.hex, `${color.hex} ${bgSize + ((++count) * paletteColorSize)}%` ]))
            .reduce((stops, arr) => stops.concat(arr), [])
            .reduce((stops, arr) => stops.concat(arr), []);

        const colorStops = [ this.backgroundColor.hex, `${this.backgroundColor.hex} ${bgSize}%` ].concat(paletteColors);

        return `linear-gradient(to right, ${colorStops.join(', ')})`;
    }

    public updateStats(stats: ColorPaletteSetStats) {
        this.currentStats = stats;
        this.updateInfoUI();
    }

    public setOpenState(isOpen: boolean): void {
        this.$dropdownLauncher.classList.toggle('open', isOpen);
    }

    public setActivePalette(palette?: ColorPalette | null): void {
        this.palettes.forEach(p => p.setActiveState(p === palette));
    }

    /**
     * This is separate from setActivePalette because not all display modes require
     * a color palette, and some colors span multiple palettes
     */
    public setActiveColor(color?: DisplayModeColorValue | null): void {
        const paletteColorMap: Record<ColorPalette['id'], PaletteColorIndex[]> = {};
        let activateBg = false;

        color?.colors.forEach((color) => {
            if (color.value === 'background') {
                activateBg = true;
                return;
            }

            if (color.value === 'transparent') {
                return;
            }

            const map = paletteColorMap[color.value.palette.id] = paletteColorMap[color.value.palette.id] || [];
            map.push(color.value.index);
        });

        this.palettes.forEach((palette) => {
            const colors = paletteColorMap[palette.id] || [];
            palette.setActiveColors(colors);
        });

        this.$bgSwatch.classList.toggle('active', activateBg);
    }

    public setType(type: ColorPaletteType): void {
        if (this.type === type) {
            return;
        }

        this.type = type;

        const colors = [ this.backgroundColor ];
        ColorPalette.convertColors(this.type, colors, (color, newColor, i) => {
            colors[i] = newColor;
            this.logger.debug(`replaced "${rgbToHex(color)}" with "${newColor.hex}" (${newColor.index})`);
        });

        if (colors[0]) {
            this.setBackgroundColor(colors[0]);
        }

        this.palettes.forEach(palette => palette.setType(type));
    }

    public getType(): ColorPaletteType {
        return this.type;
    }

    public init(): void {
        if (this.initialized) {
            return;
        }

        const $paletteList = findElement(this.$el, '.palette-list');

        this.setName(this.name);
        this.setBackgroundColor(this.backgroundColor);
        this.updateInfoUI();

        this.palettes.forEach((palette) => {
            palette.init($paletteList);
            palette.on('color_change', (color, index) => {
                this.emit('color_change', palette, color, index);
            });
        });

        this.$bgSwatch.addEventListener('click', (e) => {
            if (!(e.target instanceof HTMLElement)) {
                return;
            }

            const picker = ColorPicker.create(this.type, {
                activeColor: this.backgroundColor,
                title: 'Change background color',
            });

            picker.on('color_select', (color) => {
                this.setBackgroundColor(color);
                this.emit('bg_select', this.backgroundColor);
            });
            picker.show(e.target);
        });

        this.$dropdownLauncher.addEventListener('click', () => {
            this.emit('overflow_click', this.$dropdownLauncher);
        });

        this.$container.appendChild(this.$el);

        this.initialized = true;
    }

    public destroy(): void {
        this.$el.remove();
        this.palettes.forEach((palette) => palette.off());
    }

    public setName(newName: string): void {
        const $name = findElement(this.$el, '.palette-set-name');
        this.name = newName.trim() || `Palette Set ${this.id}`;
        $name.innerText = this.name;
    }

    public get objectCount(): number {
        return this.currentStats.objectCount;
    }

    private updateInfoUI(): void {
        findElement(this.$el, '.object-count').innerText = this.objectCount +
            ' object' + (this.objectCount === 1 ? '' : 's');
    }

    public activate(): void {
        this.$el.style.display = '';
    }

    public deactivate(): void {
        this.$el.style.display = 'none';
    }

    public setBackgroundColor(color: IndexedRGBColor): void {
        this.backgroundColor = color;
        findElement(this.$el, '.bg-color-container .color-swatch').style.backgroundColor =
            this.backgroundColor.hex;

        this.logger.debug(`ColorPaletteSet{${this.id}} set background color to ${color.index} (${color.hex})`);
    }

    public generateCode(options: CodeGenerationOptions): string {
        const indent = options.indentChar;

        const code = [
            '/*',
            `Palette${options.labelColon ? ':' : ''}` +
                (options.commentLevel >= CodeGenerationDetailLevel.Some ? ` ; "${this.name}"` : ''),
        ];

        const generateCodeLine = (color: IndexedRGBColor, label: string): string => {
            let line = `${indent}.byte ${formatAssemblyNumber(color.index, 16)}`;
            if (options.commentLevel >= CodeGenerationDetailLevel.Some) {
                line += ` ; ${label}`;
                if (options.commentLevel >= CodeGenerationDetailLevel.Lots) {
                    line += ' '.repeat(Math.max(0, 4 - label.length)) + ` - ${color.hex}`;
                }
            }

            return line;
        };

        if (isIndexedColor(this.backgroundColor)) {
            code.push(generateCodeLine(this.backgroundColor, 'BG'));
        }

        this.palettes.forEach((palette) => {
            palette.colors.forEach((color, colorIndex) => {
                if (!isIndexedColor(color)) {
                    return;
                }

                code.push(generateCodeLine(color, palette.name + 'C' + colorIndex));
            });
        });

        code.push('*/');

        return code.join('\n');
    }

    public toJSON(): ColorPaletteSetSerialized {
        return {
            id: this.id,
            name: this.name,
            backgroundColor: colorToJson(this.backgroundColor, this.type),
            palettes: this.palettes.map(palette => palette.toJSON()),
            type: this.type,
        };
    }

    public static fromJSON(json: object, mountEl: HTMLElement): ColorPaletteSet {
        this.ensureSerialized(json);

        return new ColorPaletteSet({
            id: String(json.id),
            mountEl,
            palettes: json.palettes,
            backgroundColor: json.backgroundColor,
            name: json.name,
            type: json.type || 'rgb',
        });
    }

    public static ensureSerialized(json: any): asserts json is ColorPaletteSetSerialized {
        const context: SerializationContext = 'ColorPaletteSet';

        if (!json.id || (typeof json.id !== 'string' && typeof json.id !== 'number')) {
            throw new SerializationTypeError(context, 'id', 'non-empty string or number', json.id);
        }
        if (typeof json.name !== 'string') {
            throw new SerializationTypeError(context, 'name', 'string', json.name);
        }
        if (!Array.isArray(json.palettes) && !json.palettes.every((item: unknown) => typeof item === 'object')) {
            throw new SerializationTypeError(context, 'palettes', 'array of objects');
        }
    }
}
